import { InfiniteData, QueryKey, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { PaginatedData } from '../@types';
import { ApiService, postData } from '../utils/api';

type Props<T> = Omit<
  UseInfiniteQueryOptions<PaginatedData<T>, Error, InfiniteData<PaginatedData<T>, number>, QueryKey, number>,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
> & {
  queryKey: QueryKey;
  endpoint: string;
  bodyParams?: {
    limit?: number;
    offset?: number;
    [key: string]: unknown;
  };
  apiService?: ApiService;
  customHeaders?: Record<string, string>;
  totalCount?: number;
};

const useInfinitePostQuery = <T = unknown>({
  queryKey,
  endpoint,
  bodyParams,
  apiService = 'feed',
  customHeaders,
  totalCount,
  ...options
}: Props<T>) => {
  const observer = useRef<IntersectionObserver | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isPending: isLoading,
    isFetching,
    isSuccess,
    isFetchingNextPage,
  } = useInfiniteQuery<PaginatedData<T>, Error, InfiniteData<PaginatedData<T>, number>, QueryKey, number>({
    queryKey: [...queryKey, bodyParams],
    queryFn: ({ pageParam = 0 }): Promise<PaginatedData<T>> =>
      postData(
        endpoint,
        {
          offset: pageParam,
          limit: bodyParams?.limit ?? 5,
          ...bodyParams,
        },
        apiService,
        customHeaders
      ),
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage.items || lastPage.items.length === 0) {
        return undefined;
      }
      const count = totalCount ?? lastPage.count ?? 0;
      const loadedCount = pages.reduce((total, page) => total + (page.items?.length ?? 0), 0);

      return loadedCount < count ? loadedCount : undefined;
    },
    initialPageParam: 0,
    ...options,
  });

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading || isFetching || isFetchingNextPage) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const items = useMemo(() => data?.pages.flatMap((page) => page.items ?? []) ?? [], [data]);

  return {
    data,
    items,
    totalCount: data?.pages[0]?.count ?? 0,
    isLoading,
    isFetching,
    isSuccess,
    isFetchingNextPage,
    lastElementRef,
    isInitialLoading: isLoading,
    hasNextPage,
    loadMore,
  };
};

export default useInfinitePostQuery;
